/**
 * Deterministic procedural map layout — no AI viz.
 *
 * Renders a stylized "watercolor landscape" map in the style of
 * the design mockups (dark-themed): organic islands floating
 * over a backdrop of distant mountains, scattered pine trees,
 * and a winding river, with stations placed on the islands and
 * dashed paths connecting them.
 *
 * Mountains + rocks come from a curated SVG template library
 * (see ``mapTemplates.ts``).  The procedural layer here just
 * picks which template to drop at each slot — the templates
 * themselves give the hand-drawn watercolor feel we couldn't
 * reach with pure procedural geometry.
 *
 * Algorithm is fully deterministic — same input always produces
 * the same output.  Different station sets produce visibly
 * different maps because positions + template choices are all
 * seeded by station ids.
 */

import {
  MOUNTAIN_TEMPLATES,
  ROCK_TEMPLATES,
  TREE_TEMPLATES,
} from "./mapTemplates";

// 32-bit FNV-1a hash for short strings.  Cheap, deterministic.
function hash32(str: string, salt: string = ""): number {
  let h = 0x811c9dc5;
  const s = salt + "|" + str;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** Normalize a hash to [0, 1). */
function unit(str: string, salt: string): number {
  return hash32(str, salt) / 0x1_0000_0000;
}

/** Mulberry32 PRNG seeded from a 32-bit integer. */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 0x1_0000_0000;
  };
}

// ── Public types ────────────────────────────────────────────────────

export interface MapStation {
  /** Stable id (questId or milestoneId). */
  id: string;
  /** Category grouping for clustering + same-category connections.
   * Use a constant string for ungrouped maps. */
  categoryId: string;
  /** Position index for tie-breaking; lower = earlier station. */
  order: number;
}

export interface MapPosition {
  id: string;
  /** Normalized [0, 1] coordinates within the viewbox. */
  x: number;
  y: number;
  /** Category accent color key for theming the station. */
  categoryId: string;
}

export interface MapEdge {
  from: string;
  to: string;
}

export interface MapIsland {
  /** Station this island sits under (positions[].id). */
  stationId: string;
  /** SVG path d= for the organic island blob (normalized coords). */
  path: string;
  /** A slightly larger underlying "shadow" blob path. */
  shadowPath: string;
}

/** One mountain placed on the map.  References a curated SVG
 * template from ``mapTemplates.MOUNTAIN_TEMPLATES`` rather than
 * carrying a procedurally-computed path — the templates give
 * the hand-drawn watercolor look the procedural geometry
 * couldn't approximate. */
export interface MapMountainInstance {
  templateIdx: number;
  /** Anchor in normalized coords: center-bottom of the
   * template box lands at (cx, cy). */
  cx: number;
  cy: number;
  /** Width in normalized coords; height matches. */
  width: number;
  flipX: boolean;
  /** Layer depth (0 = furthest, 2 = closest). */
  depth: number;
}

/** One pine tree placed on the map.  References a curated PNG
 * template from ``mapTemplates.TREE_TEMPLATES``. */
export interface MapTreeInstance {
  templateIdx: number;
  cx: number;
  cy: number;
  /** Width in normalized coords (height derives from template aspect). */
  width: number;
  flipX: boolean;
}

export interface MapDot {
  cx: number;
  cy: number;
  /** Normalized radius. */
  size: number;
}

/** One rock cluster placed on the map.  References a curated
 * template from ``mapTemplates.ROCK_TEMPLATES``. */
export interface MapRockInstance {
  templateIdx: number;
  cx: number;
  cy: number;
  width: number;
  flipX: boolean;
}

/** Centroid of a category's stations — used by the renderer
 * to anchor a per-category label/halo on the map so the
 * grouping is visually obvious without reading a legend. */
export interface MapCategoryCluster {
  categoryId: string;
  cx: number;
  cy: number;
  /** Radius covering all of this category's stations. */
  radius: number;
}

export interface ProceduralMap {
  positions: MapPosition[];
  edges: MapEdge[];
  islands: MapIsland[];
  mountains: MapMountainInstance[];
  trees: MapTreeInstance[];
  rocks: MapRockInstance[];
  dots: MapDot[];
  categoryClusters: MapCategoryCluster[];
  /** Suggested viewbox aspect ratio (width / height). */
  aspect: number;
}

interface BuildOptions {
  /** Seed string mixed into hashing so the same station set on
   * different maps (domain vs milestone) lays out differently. */
  seed: string;
  /** Whether to connect same-category stations with edges. */
  connectByCategory?: boolean;
  /** Whether to connect each station to the next by `order`. */
  connectSequential?: boolean;
  /** Target viewbox aspect (width / height).  Defaults to 1.5
   * (landscape, desktop).  Pass a value < 1 for a portrait layout
   * — the generator swaps its primary flow axis so the islands
   * spread down the tall dimension instead of across. */
  aspect?: number;
  /** Viewbox width the renderer will use.  Lets the generator
   * size islands in node-relative units (defaults to 1000). */
  viewW?: number;
  /** Station puck radius, in viewbox units, that the renderer
   * draws.  Islands are sized to clearly enclose it (default 30). */
  nodeRadius?: number;
}

// ── Geometry helpers ────────────────────────────────────────────────

/**
 * Catmull-Rom-style closed organic blob centered on (cx, cy) with
 * elliptical base radii (rx, ry) and 12 vertices whose distances
 * from the center are perturbed by a deterministic hash so each
 * island looks slightly different.
 */
function buildIslandPath(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  seed: string,
  variation: number = 0.42,
): string {
  const N = 16;
  // Per-vertex radius perturbation in [1 - v/2, 1 + v/2].
  const rawR: number[] = [];
  for (let i = 0; i < N; i++) {
    rawR.push(1 - variation / 2 + variation * unit(seed, `r${i}`));
  }
  // 1-2-1 smoothing pass on the radii.  Adjacent vertices with
  // very different radii produce pointy peaks in the Catmull-Rom
  // curve; this low-pass keeps the slower, organic irregularity
  // but kills the highest-frequency spikes, so silhouettes read
  // as soft curves rather than spikes.
  const r: number[] = [];
  for (let i = 0; i < N; i++) {
    const prev = rawR[(i - 1 + N) % N];
    const curr = rawR[i];
    const next = rawR[(i + 1) % N];
    r.push((prev + 2 * curr + next) / 4);
  }
  const points: [number, number][] = [];
  for (let i = 0; i < N; i++) {
    const angle = (i / N) * 2 * Math.PI;
    points.push([cx + Math.cos(angle) * rx * r[i], cy + Math.sin(angle) * ry * r[i]]);
  }
  // Closed Catmull-Rom → cubic Bezier.
  const out: string[] = [];
  out.push(`M ${points[0][0].toFixed(2)} ${points[0][1].toFixed(2)}`);
  for (let i = 0; i < N; i++) {
    const p0 = points[(i - 1 + N) % N];
    const p1 = points[i];
    const p2 = points[(i + 1) % N];
    const p3 = points[(i + 2) % N];
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    out.push(
      `C ${c1x.toFixed(2)} ${c1y.toFixed(2)} ` +
      `${c2x.toFixed(2)} ${c2y.toFixed(2)} ` +
      `${p2[0].toFixed(2)} ${p2[1].toFixed(2)}`,
    );
  }
  out.push("Z");
  return out.join(" ");
}

/**
 * Smooth cubic-Bezier path through an ordered list of normalized
 * positions.  Used by the capability detail view to render the
 * river/path that connects the milestones — the path starts at
 * the first milestone, ends at the last, and bends naturally
 * through the rest without stretching beyond the endpoints.
 */
export function pathThroughPositions(
  positions: Array<{ x: number; y: number }>,
): string {
  if (positions.length === 0) return "";
  if (positions.length === 1) {
    return `M ${positions[0].x.toFixed(3)} ${positions[0].y.toFixed(3)}`;
  }
  let path = `M ${positions[0].x.toFixed(3)} ${positions[0].y.toFixed(3)}`;
  for (let i = 1; i < positions.length; i++) {
    const prev = positions[i - 1];
    const curr = positions[i];
    // Cubic bezier with control points horizontal-offset from the
    // endpoints; gives a soft S-curve rather than a straight line.
    const c1x = (prev.x + curr.x) / 2;
    const c2x = (prev.x + curr.x) / 2;
    path += (
      ` C ${c1x.toFixed(3)} ${prev.y.toFixed(3)}` +
      ` ${c2x.toFixed(3)} ${curr.y.toFixed(3)}` +
      ` ${curr.x.toFixed(3)} ${curr.y.toFixed(3)}`
    );
  }
  return path;
}

// ── Main builder ────────────────────────────────────────────────────

export function buildProceduralMap(
  stations: MapStation[],
  options: BuildOptions,
): ProceduralMap {
  const { seed, connectByCategory = true, connectSequential = false } = options;
  // Landscape (desktop) maps stay at ~1.5; mobile passes a
  // portrait aspect (< 1).  ``portrait`` flips the primary flow
  // axis so islands march DOWN a tall viewport rather than across.
  const aspect = options.aspect ?? 1.5;
  const portrait = aspect < 1;
  const viewW = options.viewW ?? 1000;
  const viewH = viewW / aspect;
  const nodeRadius = options.nodeRadius ?? 30;
  // Island geometry, in normalized coords.  Sized in node-radius
  // multiples so the island always reads as a base that clearly
  // ENCLOSES the station puck — wider than tall for a flat-blob
  // look, but taller than the node in both dimensions.
  const islandRx = (nodeRadius * 2.6) / viewW;
  const islandRy = (nodeRadius * 1.7) / viewH;
  const n = stations.length;
  if (n === 0) {
    return {
      positions: [],
      edges: [],
      islands: [],
      mountains: [],
      trees: [],
      rocks: [],
      dots: [],
      categoryClusters: [],
      aspect,
    };
  }

  // ── Position layout (clustered by category, with relaxation) ──
  const byCategory = new Map<string, MapStation[]>();
  for (const s of stations) {
    if (!byCategory.has(s.categoryId)) byCategory.set(s.categoryId, []);
    byCategory.get(s.categoryId)!.push(s);
  }
  const categoryIds = Array.from(byCategory.keys()).sort(
    (a, b) => hash32(a, seed) - hash32(b, seed),
  );

  const categoryCenters = new Map<string, { x: number; y: number }>();
  const cCount = categoryIds.length;
  categoryIds.forEach((cid, idx) => {
    const t = (idx + 0.5) / cCount;
    // ``primary`` marches categories along the map's long axis;
    // ``cross`` alternates them between two lanes on the short
    // axis.  Landscape → primary is x; portrait → primary is y.
    const primary = 0.12 + 0.76 * t;
    const crossJitter = (unit(cid, seed + "-cross") - 0.5) * 0.3;
    const cross = (idx % 2 === 0 ? 0.4 : 0.62) + crossJitter;
    categoryCenters.set(
      cid,
      portrait ? { x: cross, y: primary } : { x: primary, y: cross },
    );
  });

  const positions: MapPosition[] = [];
  if (cCount === 1 && connectSequential) {
    // Single-category sequential layout (e.g. the capability
    // detail's milestone sub-map): distribute stations along the
    // long axis with a varied cross-axis SHAPE so each quest's
    // sub-map reads as its own organic chain — not the same
    // sinusoid every time.  One of SEVEN patterns is picked per
    // seed, with per-seed parameter jitter inside each.  Some
    // patterns also jitter the primary axis so node SPACING feels
    // hand-placed (not uniformly metered) — the path then
    // changes direction multiple times across the canvas.
    const items = byCategory.get(categoryIds[0])!;
    items.sort((a, b) => a.order - b.order);
    const N = items.length;

    const PATTERN_COUNT = 7;
    const patternIdx = Math.floor(unit(seed, "subpattern") * PATTERN_COUNT);
    // Per-pattern parameter draws (deterministic per seed).
    const freq = 1.0 + unit(seed, "freq") * 1.6;             // 1.0 - 2.6
    const phase = unit(seed, "phase") * 2 * Math.PI;
    const waveAmp = 0.16 + unit(seed, "wave-amp") * 0.12;    // 0.16 - 0.28
    const arcAmp = (unit(seed, "arc-sign") > 0.5 ? 1 : -1)
      * (0.20 + unit(seed, "arc-amp") * 0.14);               // ±(0.20-0.34)
    const diagAsc = unit(seed, "diag") > 0.5;
    const diagLo = 0.20 + unit(seed, "diag-base") * 0.10;    // 0.20 - 0.30
    const diagHi = 0.68 + unit(seed, "diag-top") * 0.12;     // 0.68 - 0.80
    const zigLo = 0.22 + unit(seed, "zig-lo") * 0.10;        // 0.22 - 0.32
    const zigHi = 0.66 + unit(seed, "zig-hi") * 0.10;        // 0.66 - 0.76
    const bowSkew = 0.25 + unit(seed, "bow-skew") * 0.5;     // peak at 0.25-0.75
    const bowSign = unit(seed, "bow-sign") > 0.5 ? 1 : -1;
    const bowAmp = 0.22 + unit(seed, "bow-amp") * 0.12;      // 0.22 - 0.34
    const multiFreq = 2.0 + unit(seed, "mw-freq") * 1.6;     // 2.0 - 3.6
    const multiAmp = 0.20 + unit(seed, "mw-amp") * 0.10;     // 0.20 - 0.30

    // Pre-compute the WANDERING pattern: per-node cross is a
    // deterministic random value, but each is flipped to the
    // OPPOSITE side from the previous if they'd land too close
    // — guarantees consecutive nodes are on visibly opposing
    // sides of the canvas, so the path changes direction.
    const wanderCross: number[] = [];
    {
      let prev: number | null = null;
      for (let i = 0; i < N; i++) {
        let raw = unit(seed, `w${i}`);
        if (prev !== null && Math.abs(raw - prev) < 0.35) raw = 1 - raw;
        wanderCross.push(raw);
        prev = raw;
      }
    }

    const crossFor = (t: number, idx: number): number => {
      switch (patternIdx) {
        case 0:  // meandering sinusoid
          return 0.5 + Math.sin(t * freq * Math.PI + phase) * waveAmp;
        case 1: {
          // gentle arc — parabola peaking at the middle
          const arc = 1 - 4 * (t - 0.5) * (t - 0.5);
          return 0.5 + arcAmp * arc;
        }
        case 2:  // diagonal traverse, ascending or descending
          return diagAsc ? diagLo + (diagHi - diagLo) * t
                         : diagHi - (diagHi - diagLo) * t;
        case 3:  // zig-zag between two cross lanes
          return idx % 2 === 0 ? zigLo : zigHi;
        case 4: {
          // wandering — multi-direction chain, opposing sides
          // enforced so each step visibly turns.
          const raw = wanderCross[idx] ?? 0.5;
          return 0.22 + raw * 0.56;
        }
        case 5: {
          // asymmetric bow — peak shifted off-center so the
          // path "leans" rather than mirroring around the middle.
          const half = Math.max(bowSkew, 1 - bowSkew);
          const dx = (t - bowSkew) / half;
          const bow = Math.max(0, 1 - dx * dx);
          return 0.5 + bowSign * bowAmp * bow;
        }
        case 6:
        default:
          // multi-wave — faster sinusoid with a bigger swing,
          // gives 2-3 visible direction changes across the chain.
          return 0.5 + Math.sin(t * multiFreq * Math.PI + phase) * multiAmp;
      }
    };

    // Wandering also jitters the PRIMARY axis so node spacing
    // doesn't read as metered — the path feels hand-placed.
    const primaryFor = (t: number, idx: number): number => {
      if (patternIdx === 4) {
        const pj = (unit(seed, `wp${idx}`) - 0.5) * 0.10;
        return Math.max(0.12, Math.min(0.88, 0.12 + 0.76 * t + pj));
      }
      return 0.12 + 0.76 * t;
    };

    items.forEach((s, idx) => {
      const t = N === 1 ? 0.5 : (idx + 0.5) / N;
      const primary = primaryFor(t, idx);
      const jitter = (unit(s.id, seed + "-jit") - 0.5) * 0.05;
      const cross = crossFor(t, idx) + jitter;
      positions.push({
        id: s.id,
        x: portrait ? cross : primary,
        y: portrait ? primary : cross,
        categoryId: s.categoryId,
      });
    });
  } else {
    // Place same-category stations in a tight radial arrangement
    // around their category center, so the grouping is visually
    // obvious before the relaxation pass adjusts for overlaps.
    for (const cid of categoryIds) {
      const center = categoryCenters.get(cid)!;
      const items = byCategory.get(cid)!;
      items.sort((a, b) => a.order - b.order);
      const count = items.length;
      items.forEach((s, idx) => {
        if (count === 1) {
          // Singleton: just sit at the center.
          positions.push({
            id: s.id, x: center.x, y: center.y, categoryId: s.categoryId,
          });
          return;
        }
        // Multi-station: arrange radially with a small jitter
        // so each member gets a unique slot.  Radius is small
        // enough that the cluster reads as one group; the
        // relaxation pass below resolves any overlap.
        // Portrait clusters start at the top (angle −π/2) and
        // compress hard on x, so a category's stations stack
        // vertically — that keeps each station's (centered,
        // below-the-node) label from colliding with a sibling's.
        const angleBase = (idx / count) * 2 * Math.PI
          + (portrait ? -Math.PI / 2 : 0);
        const angleJit = (unit(s.id, "ang") - 0.5) * 0.6;
        const angle = angleBase + angleJit;
        const r = 0.075 + 0.035 * unit(s.id, "r");
        // Compress the cluster along the SHORT axis so it reads as
        // one tight group and matches the viewbox proportions.
        let ox = Math.cos(angle) * r;
        let oy = Math.sin(angle) * r;
        if (portrait) ox *= 0.55;
        else oy *= 0.75;
        positions.push({
          id: s.id,
          x: center.x + ox,
          y: center.y + oy,
          categoryId: s.categoryId,
        });
      });
    }
  }

  // Relaxation: enforce visual non-overlap, with same-category
  // stations allowed to touch (tangent) but different-category
  // stations clearly separated.  Distances are measured in
  // viewbox-y-equivalent units (x is multiplied by aspect to
  // correct for the wider horizontal viewport).
  //
  // Island visual half-radius in viewbox-y units ≈ rx * aspect
  // * max_variation = 0.075 * 1.5 * 1.22 ≈ 0.137, so two islands
  // are visually tangent at ~0.27 and clearly separated at ~0.35.
  // ── Relaxation: enforce non-overlap with two distinct policies.
  //
  // Same-category islands should read as neighbouring "plates"
  // that share a side — almost touching, with bulges interlocking
  // and concavities leaving a tiny gap.  Their relaxation is
  // direction-aware via the island ellipse so a vertical pair is
  // just as tight as a horizontal pair (a single scalar minDist
  // would over-separate the short axis).
  //
  // Different-category islands stay clearly apart on the scalar
  // minDist (conservative, easy).
  const islandReach = islandRx * aspect;
  const spread = portrait ? 1.3 : 1.0;
  const crossCatMinDist = islandReach * 3.1 * spread;
  // Ellipse-overlap target: 1 = tangent, > 1 = gap.  Same-category
  // islands carry organic bulges up to ~1.21× the mean radius; we
  // sit well above 2× that (target = 1.40) so even a 3-cluster
  // (where multiple bulges can collide pairwise) keeps clear
  // visible gaps on mobile.  Still much tighter than
  // ``crossCatMinDist`` — same-category islands cluster, different
  // categories stay clearly apart.
  const sameCatEllipseTarget = 1.40;
  for (let iter = 0; iter < 120; iter++) {
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const a = positions[i];
        const b = positions[j];
        if (a.categoryId === b.categoryId) {
          // Elliptical separation: e = √((Δx/2rx)² + (Δy/2ry)²).
          // e<1 → ellipses overlap; e≥target → tiny gap.
          const nx = (a.x - b.x) / (2 * islandRx);
          const ny = (a.y - b.y) / (2 * islandRy);
          const e = Math.sqrt(nx * nx + ny * ny);
          if (e < sameCatEllipseTarget && e > 1e-4) {
            // Push along the connecting line by scaling outward
            // from the midpoint — new e becomes target * 1.
            const factor = sameCatEllipseTarget / e;
            const midX = (a.x + b.x) / 2;
            const midY = (a.y + b.y) / 2;
            a.x = midX + (a.x - midX) * factor;
            a.y = midY + (a.y - midY) * factor;
            b.x = midX + (b.x - midX) * factor;
            b.y = midY + (b.y - midY) * factor;
          }
        } else {
          const dx = (a.x - b.x) * aspect;
          const dy = a.y - b.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < crossCatMinDist && d > 0.0001) {
            const push = (crossCatMinDist - d) / 2;
            const nx = (dx / d) * push;
            const ny = (dy / d) * push;
            a.x += nx / aspect;
            a.y += ny;
            b.x -= nx / aspect;
            b.y -= ny;
          }
        }
      }
    }
  }
  // Clamp inside the frame — the tighter margin goes on the SHORT
  // axis (less room to spare), the looser on the long axis.
  const longLo = 0.1, longHi = 0.9;
  const shortLo = 0.16, shortHi = 0.86;
  for (const p of positions) {
    if (portrait) {
      p.x = Math.max(shortLo, Math.min(shortHi, p.x));
      p.y = Math.max(longLo, Math.min(longHi, p.y));
    } else {
      p.x = Math.max(longLo, Math.min(longHi, p.x));
      p.y = Math.max(shortLo, Math.min(shortHi, p.y));
    }
  }

  // ── Edges ─────────────────────────────────────────────────────
  const edges: MapEdge[] = [];
  if (connectByCategory) {
    for (const cid of categoryIds) {
      const items = positions.filter((p) => p.categoryId === cid);
      for (let i = 1; i < items.length; i++) {
        let best = items[0];
        let bestD = Infinity;
        for (let j = 0; j < i; j++) {
          const dx = (items[i].x - items[j].x) * aspect;
          const dy = items[i].y - items[j].y;
          const d = dx * dx + dy * dy;
          if (d < bestD) { bestD = d; best = items[j]; }
        }
        edges.push({ from: best.id, to: items[i].id });
      }
    }
    for (let i = 1; i < categoryIds.length; i++) {
      const a = positions.find((p) => p.categoryId === categoryIds[i - 1])!;
      const b = positions.find((p) => p.categoryId === categoryIds[i])!;
      if (a && b) edges.push({ from: a.id, to: b.id });
    }
  }
  if (connectSequential) {
    const ordered = [...stations].sort((a, b) => a.order - b.order);
    for (let i = 1; i < ordered.length; i++) {
      edges.push({ from: ordered[i - 1].id, to: ordered[i].id });
    }
  }

  // ── Islands (organic blobs under each station) ────────────────
  // Slightly tighter than before — paired with the new relaxation
  // distances, this keeps islands clearly separated when their
  // categories differ.
  const islands: MapIsland[] = positions.map((p) => ({
    stationId: p.id,
    path: buildIslandPath(p.x, p.y, islandRx, islandRy, p.id + "|" + seed),
    shadowPath: buildIslandPath(
      p.x, p.y, islandRx * 1.2, islandRy * 1.2,
      p.id + "|" + seed + "|sh", 0.3,
    ),
  }));

  // ── Background decoration (mountains, trees, river, dots) ──────
  const decorationSeed = stations.map((s) => s.id).join("|");
  const rand = mulberry32(hash32(decorationSeed, seed + "-deco"));

  // Mountain instances — pick template indices deterministically
  // for each slot.  Templates live in ``mapTemplates.ts`` (sharp
  // peak, rounded peak, two-peak range, snow-cap, etc.) and the
  // renderer instantiates a scaled + optionally flipped copy at
  // each (cx, cy) anchor.
  //
  // Mountains are scattered across the WHOLE map — not just a top
  // horizon band — with larger ranges up high and smaller
  // mid-ground peaks lower down.  Each placement skips spots too
  // close to a station so mountains never crowd an island/label.
  const mountains: MapMountainInstance[] = [];
  const mountainCount = 8 + Math.floor(rand() * 5);
  let mountainTries = 0;
  while (
    mountains.length < mountainCount &&
    mountainTries < mountainCount * 8
  ) {
    mountainTries++;
    const cx = 0.04 + rand() * 0.92;
    const cy = 0.16 + rand() * 0.66;
    const depth = Math.floor(rand() * 3);
    // Big ranges live in the upper "horizon" band; peaks placed
    // further down are small/medium so they read as mid-ground
    // hills rather than a wall in front of the islands.
    const w = cy < 0.34
      ? 0.15 + rand() * 0.16 + depth * 0.03
      : 0.06 + rand() * 0.08 + depth * 0.02;
    // Skip placements that sit right on a station.  Mountains
    // render BEHIND the islands + label pills, so they can sit
    // fairly close — this keep-out only stops a peak rising
    // awkwardly right beside a node.
    const tooClose = positions.some((p) => {
      const dx = (cx - p.x) * aspect;
      const dy = cy - p.y;
      return dx * dx + dy * dy < 0.1 * 0.1;
    });
    if (tooClose) continue;
    mountains.push({
      templateIdx: Math.floor(rand() * MOUNTAIN_TEMPLATES.length),
      cx,
      cy,
      width: w,
      flipX: rand() > 0.5,
      depth,
    });
  }

  // Pine tree instances — same template-driven approach as
  // mountains/rocks.  Avoids placements too close to stations so
  // the trees never obscure an island.  No-op when no tree
  // PNGs are present in ``data/map_assets/``.
  const trees: MapTreeInstance[] = [];
  if (TREE_TEMPLATES.length > 0) {
    const treeCount = 14 + Math.floor(rand() * 10);
    let treeTries = 0;
    while (trees.length < treeCount && treeTries < treeCount * 6) {
      treeTries++;
      const cx = rand();
      const cy = 0.34 + rand() * 0.58;
      const tooClose = positions.some((p) => {
        const dx = (cx - p.x) * aspect;
        const dy = cy - p.y;
        return dx * dx + dy * dy < 0.13 * 0.13;
      });
      if (tooClose) continue;
      trees.push({
        templateIdx: Math.floor(rand() * TREE_TEMPLATES.length),
        cx,
        cy,
        // Smaller than before — large trees stood out more than the
        // islands they're meant to decorate.
        width: 0.022 + rand() * 0.014,
        flipX: rand() > 0.5,
      });
    }
  }

  // Rock instances — same template-driven approach as mountains.
  // Templates are tiny silhouettes (single boulder, pair,
  // 3-pebble row, angular cluster, etc.) that the renderer
  // instantiates at the chosen anchor.
  const rocks: MapRockInstance[] = [];
  const rockCount = 10 + Math.floor(rand() * 8);
  let rockTries = 0;
  while (rocks.length < rockCount && rockTries < rockCount * 6) {
    rockTries++;
    const cx = rand();
    const cy = 0.32 + rand() * 0.62;
    // Skip placements too close to any station/island.
    const tooClose = positions.some((p) => {
      const dx = (cx - p.x) * aspect;
      const dy = cy - p.y;
      return dx * dx + dy * dy < 0.13 * 0.13;
    });
    if (tooClose) continue;
    rocks.push({
      templateIdx: Math.floor(rand() * ROCK_TEMPLATES.length),
      cx,
      cy,
      width: 0.032 + rand() * 0.028,
      flipX: rand() > 0.5,
    });
  }

  // Faint dots (sparkles / sand specks).
  const dots: MapDot[] = [];
  const dotCount = 16 + Math.floor(rand() * 12);
  for (let i = 0; i < dotCount; i++) {
    dots.push({
      cx: rand(),
      cy: rand(),
      size: 0.003 + rand() * 0.005,
    });
  }

  // Category cluster centroids — used by the renderer to anchor
  // a per-category label/halo so the color↔category mapping is
  // visible without having to consult a separate legend.
  const categoryClusters: MapCategoryCluster[] = [];
  for (const cid of categoryIds) {
    const items = positions.filter((p) => p.categoryId === cid);
    if (items.length === 0) continue;
    const cx = items.reduce((s, p) => s + p.x, 0) / items.length;
    const cy = items.reduce((s, p) => s + p.y, 0) / items.length;
    let maxR = 0;
    for (const p of items) {
      const dx = (p.x - cx) * aspect;
      const dy = p.y - cy;
      const r = Math.sqrt(dx * dx + dy * dy);
      if (r > maxR) maxR = r;
    }
    categoryClusters.push({
      categoryId: cid,
      cx,
      cy,
      // Add a buffer so the halo encompasses the island bodies,
      // not just the station centers.
      radius: maxR + 0.11,
    });
  }

  return {
    positions,
    edges,
    islands,
    mountains,
    trees,
    rocks,
    dots,
    categoryClusters,
    aspect,
  };
}
